// BM-H80 P2 — FeeEarnerFormReview page (authenticated).
//
// Route: /matters/:matterRef/client-form-review/:form_instance_id
//
// Fee-earner surface. Loads all forms for the matter, lets the fee
// earner drill into one submitted form and see the client's answers
// alongside the EngineResultT surface (HARD_STOPs, advisories,
// cross-engine LPA advisory). Provides revoke, extend, resend, and
// finalise actions.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { clientFormsApi } from "../api/clientForms";
import ClientAnswersSummary from "../components/client-forms/ClientAnswersSummary";
import DerivedSummaryPanel from "../components/client-forms/DerivedSummaryPanel";
import EngineResultView, { toEngineViewShape } from "../components/client-forms/EngineResultView";
import { errorMessage } from "../utils/errorMessage";

function StateBadge({ state }) {
  const palette = {
    invited: { bg: "#e0f2fe", fg: "#075985" },
    in_progress: { bg: "#fef3c7", fg: "#78350f" },
    submitted: { bg: "#dcfce7", fg: "#166534" },
    under_review: { bg: "#e0e7ff", fg: "#3730a3" },
    finalised: { bg: "#f1f5f9", fg: "#334155" },
    revoked: { bg: "#fee2e2", fg: "#991b1b" },
  }[state] || { bg: "#f1f5f9", fg: "#334155" };
  return (
    <span
      data-testid={`state-badge-${state}`}
      style={{
        display: "inline-block",
        padding: "2px 10px",
        background: palette.bg,
        color: palette.fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {state}
    </span>
  );
}

export default function FeeEarnerFormReview() {
  const { matterRef, form_instance_id: formId } = useParams();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await clientFormsApi.listForMatter(matterRef);
      setForms(data.forms || []);
      const focused = (data.forms || []).find((f) => f.form_instance_id === formId);
      setActiveForm(focused || (data.forms || [])[0] || null);
    } catch (err) {
      toast.error(errorMessage(err, "Could not load forms for this matter"));
    } finally {
      setLoading(false);
    }
  }, [matterRef, formId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRevoke() {
    if (!activeForm) return;
    setBusy(true);
    try {
      await clientFormsApi.revokeToken(matterRef, activeForm.form_instance_id);
      toast.success("Every access token for this form has been revoked.");
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "Revoke failed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleExtend() {
    if (!activeForm) return;
    setBusy(true);
    try {
      const { data } = await clientFormsApi.extendExpiry(matterRef, activeForm.form_instance_id, 30);
      toast.success(`New link issued, expires ${new Date(data.expires_at).toLocaleString()}`);
    } catch (err) {
      toast.error(errorMessage(err, "Extend failed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    if (!activeForm) return;
    setBusy(true);
    try {
      await clientFormsApi.resendToken(matterRef, activeForm.form_instance_id, 30);
      toast.success("A fresh invite has been emailed to the client.");
    } catch (err) {
      toast.error(errorMessage(err, "Resend failed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleFinalise() {
    if (!activeForm) return;
    setBusy(true);
    try {
      await clientFormsApi.finaliseForm(matterRef, activeForm.form_instance_id, null);
      toast.success("Form marked as finalised.");
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "Finalise failed"));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div data-testid="fee-earner-review-loading" style={{ padding: 40 }}>
        Loading forms for this matter...
      </div>
    );
  }

  if (!activeForm) {
    return (
      <div data-testid="fee-earner-review-empty" style={{ padding: 40 }}>
        No client-facing forms have been created for this matter yet.
      </div>
    );
  }

  // BM-H4056 - the LPA engine stores a flat {hard_stops, advisories} result, which
  // EngineResultView (written for the wills {flags: {...}} shape) read as empty, so every LPA
  // showed "No blocking issues found so far" whatever the engine had found.
  const engineResult = toEngineViewShape(activeForm.engine_result?.result || null);
  const answers = activeForm.answers || {};

  return (
    <div data-testid="fee-earner-form-review" style={{ padding: "24px 20px", maxWidth: 1200, margin: "0 auto" }}>
      <header style={{ marginBottom: 16 }}>
        <button
          type="button"
          data-testid="fee-earner-back-btn"
          onClick={() => navigate(-1)}
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            color: "var(--text, #334155)",
          }}
        >
          Back
        </button>
        {/* BM-H4056 - was #0f172a, near invisible on the dark app background. */}
        <h1 style={{ marginTop: 12, fontSize: 22, color: "var(--text, #0f172a)" }}>
          Client form review · {matterRef}
        </h1>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "260px minmax(0, 1fr)",
          gap: 20,
        }}
      >
        <aside
          data-testid="fee-earner-form-list"
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            background: "#fff",
            padding: 8,
            maxHeight: 720,
            overflowY: "auto",
          }}
        >
          {forms.map((f) => {
            const isActive = f.form_instance_id === activeForm.form_instance_id;
            return (
              <button
                key={f.form_instance_id}
                type="button"
                data-testid={`form-list-row-${f.form_instance_id}`}
                onClick={() => setActiveForm(f)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: 10,
                  background: isActive ? "#4338ca" : "#fff",
                  color: isActive ? "#fff" : "#0f172a",
                  border: "1px solid",
                  borderColor: isActive ? "#4338ca" : "#e2e8f0",
                  borderRadius: 6,
                  marginBottom: 6,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {f.client_name || f.client_email}
                </div>
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                  {kindLabel(f.engine_kind)} · {stateLabel(f.state)}
                </div>
              </button>
            );
          })}
        </aside>

        <main style={{ minWidth: 0 }}>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 20,
              background: "#fff",
              marginBottom: 20,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                {/* BM-H4277: the questionnaire's name, not its internal id (Liezel's 1562 review showed the id). */}
                <div style={{ fontSize: 12, color: "#64748b" }} title={activeForm.form_instance_id}>
                  {kindLabel(activeForm.engine_kind)} questionnaire
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: "#0f172a" }}>
                  {activeForm.client_name || activeForm.client_email}
                </div>
              </div>
              <StateBadge state={activeForm.state} />
            </div>

            <div
              data-testid="fee-earner-actions"
              style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}
            >
              <button
                type="button"
                data-testid="fee-earner-resend-btn"
                onClick={handleResend}
                disabled={busy}
                style={actionBtn(false)}
              >
                Resend invite
              </button>
              <button
                type="button"
                data-testid="fee-earner-extend-btn"
                onClick={handleExtend}
                disabled={busy}
                style={actionBtn(false)}
              >
                Extend expiry (+30d)
              </button>
              <button
                type="button"
                data-testid="fee-earner-revoke-btn"
                onClick={handleRevoke}
                disabled={busy}
                style={actionBtn(false, "#b91c1c")}
              >
                Revoke tokens
              </button>
              <button
                type="button"
                data-testid="fee-earner-finalise-btn"
                onClick={handleFinalise}
                disabled={busy || !["submitted", "under_review"].includes(activeForm.state)}
                style={actionBtn(true)}
              >
                Finalise
              </button>
            </div>
          </div>

          <DerivedSummaryPanel matterRef={matterRef} formInstanceId={activeForm.form_instance_id} engineKind={activeForm.engine_kind} />

          {/* BM-H4056 - checks first (short), then the answers at full width so the labelled
              summary has room to read. */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 20 }}>
            <section
              data-testid="fee-earner-engine"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                background: "#fff",
              }}
            >
              <h2 style={{ fontSize: 16, marginTop: 0, marginBottom: 12, color: "#0f172a" }}>
                Checks on the answers
              </h2>
              <EngineResultView engineResult={engineResult} />
            </section>
            <section
              data-testid="fee-earner-answers"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                background: "#fff",
              }}
            >
              <h2 style={{ fontSize: 16, marginTop: 0, marginBottom: 12, color: "#0f172a" }}>
                Client answers
              </h2>
              <ClientAnswersSummary answers={answers} engineKind={activeForm.engine_kind} />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

// BM-H4056 - "lpa · submitted" read as code in the form list.
function kindLabel(kind) {
  if (kind === "lpa") return "LPA";
  if (!kind || kind === "wills") return "Will";
  return kind;
}

function stateLabel(state) {
  const s = String(state || "").replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function actionBtn(primary, tone) {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    border: "1px solid",
    borderColor: tone || (primary ? "#4338ca" : "#cbd5e1"),
    background: primary ? "#4338ca" : "#fff",
    color: primary ? "#fff" : tone || "#334155",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  };
}
